"use client";

import { useEffect, useRef } from "react";

import { bezierCss, LOGO_LOADER, LOGO_LOADER_COMPACT, type LogoPiece } from "@tami/tokens";

import { LOGO_TAMI_PIECES } from "../../brands/logo-tami";
import { cn } from "../../cn";

type TamiLoadingIndicatorProps = {
  /**
   * `full` (padrão): Giro → Onda, para tela e seção. `compact`: só o Giro, em
   * 1,2 s, para botão e ícone até 24px (TAM-281) — renderiza só o SVG, que
   * herda a cor do texto como o ícone que ele substitui; `className` vai nele.
   */
  variant?: "full" | "compact";
  className?: string;
  logoClassName?: string;
  /** Giro → Onda (desligado durante o gesto de pull). */
  animated?: boolean;
  /** Fundo opaco com cantos arredondados — evita misturar com o conteúdo por baixo (PTR mobile). */
  withBackdrop?: boolean;
};

const PIECES: readonly LogoPiece[] = ["top", "bottomLeft", "right"];

/** A peça escala em torno do próprio centro, não do centro do símbolo. */
const PIECE_STYLE = { transformBox: "fill-box", transformOrigin: "center" } as const;

/**
 * Logo Tãmi animada — as peças se afastam, o símbolo gira e a onda passa por
 * elas (`LOGO_LOADER` de `@tami/tokens`). Usado em redirect, auth e
 * pull-to-refresh.
 *
 * Anima pela Web Animations API, com os quadros-chave do token: assim o web e
 * o app leem a mesma coreografia, sem uma cópia dela em CSS.
 */
export function TamiLoadingIndicator({
  variant = "full",
  className,
  logoClassName,
  animated = true,
  withBackdrop = false,
}: TamiLoadingIndicatorProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!animated || !svg || typeof svg.animate !== "function") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const timeline = variant === "compact" ? LOGO_LOADER_COMPACT : LOGO_LOADER;
    const timing = { duration: timeline.durationMs, iterations: Infinity };
    const animations = [
      svg.animate(
        timeline.spin.map((frame) => ({
          offset: frame.offset,
          transform: `rotate(${frame.rotateDeg}deg)`,
          easing: bezierCss(frame.easing),
        })),
        timing,
      ),
    ];
    for (const name of PIECES) {
      const path = svg.querySelector<SVGPathElement>(`[data-piece="${name}"]`);
      if (!path) continue;
      const track = timeline.pieces[name];
      animations.push(
        path.animate(
          track.transform.map((frame) => ({
            offset: frame.offset,
            transform: `translate(${frame.x}px, ${frame.y}px) scale(${frame.scale})`,
            easing: bezierCss(frame.easing),
          })),
          timing,
        ),
        path.animate(
          track.opacity.map((frame) => ({
            offset: frame.offset,
            opacity: frame.opacity,
            easing: bezierCss(frame.easing),
          })),
          timing,
        ),
      );
    }
    return () => animations.forEach((animation) => animation.cancel());
  }, [animated, variant]);

  const pieces = PIECES.map((name) => (
    <path
      key={name}
      data-piece={name}
      d={LOGO_TAMI_PIECES.paths[name]}
      fill="currentColor"
      style={PIECE_STYLE}
    />
  ));

  if (variant === "compact") {
    return (
      <svg
        ref={svgRef}
        viewBox={LOGO_TAMI_PIECES.viewBox}
        fill="none"
        aria-hidden
        className={cn("inline-block size-4 shrink-0 overflow-visible", className)}
      >
        {pieces}
      </svg>
    );
  }

  const indicator = (
    <div
      className={cn(
        "relative flex size-6 shrink-0 items-center justify-center overflow-visible",
        className,
      )}
    >
      <svg
        ref={svgRef}
        viewBox={LOGO_TAMI_PIECES.viewBox}
        fill="none"
        aria-hidden
        className={cn("block shrink-0 overflow-visible text-foreground", logoClassName ?? "size-6")}
      >
        {pieces}
      </svg>
    </div>
  );

  if (!withBackdrop) {
    return indicator;
  }

  return (
    <div className="relative flex size-14 shrink-0 items-center justify-center overflow-visible rounded-xl bg-background shadow-md ring-1 ring-border">
      {indicator}
    </div>
  );
}
